/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { BgLocalCopyComponent } from '@karya/core';
import { useEffect, useRef } from 'react';
import { ComponentRendererProps } from '../ComponentRenderer';

type CopyParams = {
  source?: unknown;
  source_file?: string | null;
  target?: unknown;
  target_file?: string | null;
};

export type CopyStep = 'copy-data' | 'copy-file' | 'advance';

function hasCopyableFile(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

function sameValue(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) {
    return true;
  }
  if (typeof left === 'object' && left !== null && typeof right === 'object' && right !== null) {
    try {
      return JSON.stringify(left) === JSON.stringify(right);
    } catch {
      return false;
    }
  }
  return false;
}

/** Next preview action for a local copy component. Data is copied before files. */
export function nextCopyStep(component: BgLocalCopyComponent, params: CopyParams): CopyStep {
  if (component.target && params.source !== undefined && !sameValue(params.source, params.target)) {
    return 'copy-data';
  }
  if (component.target_file && hasCopyableFile(params.source_file) && params.source_file !== params.target_file) {
    return 'copy-file';
  }
  return 'advance';
}

export function BackgroundCopyRenderer(props: ComponentRendererProps) {
  const { isCurrent, isDocked, next, writeOutput, node } = props;
  const component = node.component as BgLocalCopyComponent;
  const params = node.params as CopyParams;
  const advancedRef = useRef(false);

  useEffect(() => {
    if (!isCurrent || isDocked) {
      advancedRef.current = false;
      return;
    }

    const step = nextCopyStep(component, params);
    if (step === 'copy-data' && component.target) {
      writeOutput(component.target, params.source);
      return;
    }
    if (step === 'copy-file' && component.target_file && hasCopyableFile(params.source_file)) {
      writeOutput(component.target_file, params.source_file);
      return;
    }
    if (!advancedRef.current) {
      advancedRef.current = true;
      next();
    }
  }, [isCurrent, isDocked, component, params, next, writeOutput]);

  return null;
}
